import type { DataTableColumn } from "@carbon6/c6-frontend-design";
import { memo, useEffect, useMemo, type ReactNode } from "react";
import { budgetUnitCellLabel } from "@/components/table/CheckpointCount";
import { HatDataTable } from "@/components/table/HatDataTable";
import { formatRelativeTime } from "@/utils/datetime";
import { groupPoliciesByDataset } from "./evalPolicyGroups";
import { EvalPolicyGroupRow, EvalPolicyRow } from "./EvalPolicyTableRow";
import { EvalPolicyTableSource } from "./EvalPolicyTableSource";
import type { EvaluationTargetPolicy } from "./evaluationTargetPolicy";
import styles from "./EvalPolicyTable.module.css";

// Evaluation 대상 policy 표. 파라미터 모달과 확인 다이얼로그가 같은 표를 그린다. 학습 dataset
// 그룹이 최상위 행이다. 그 dataset 으로 학습한 policy 는 c6 renderRowChild 하위 행으로 그룹 행 바로
// 아래에 붙는다. 하위 행 셀은 표 컬럼 트랙의 subgrid 라 ID·Epoch·Created 가 헤더 컬럼에 정렬된다.
// 상대 시각의 기준 시점은 창의 열림 상태를 아는 호출부가 소유한다(rules §12).

const EMPTY_CELL = "—";

// 컬럼 순서(1 Policy, 2 ID, 3 #(체크포인트), 4 등록일)는 EvalPolicyTable.module.css 의 하위 행
// 트랙 번호와 연결된다. 바꾸면 거기도 같이 고친다. 그룹 행은 dataset 소제목만 가지므로 Policy 열
// 외에는 비운다.
const EVAL_POLICY_COLUMNS: readonly DataTableColumn<EvalPolicyGroupRow>[] = [
  {
    key: "label",
    header: "Policy",
    width: "minmax(240px, 1fr)",
    render: (row) => (
      <span className={styles.groupHead}>
        <span className={styles.groupLabel}>Dataset</span>
        <span className={styles.groupName}>{row.label}</span>
        <span className={styles.groupCount}>{`Policy ${row.policyCount}개`}</span>
      </span>
    ),
  },
  { key: "id", header: "ID", width: "110px", render: () => null },
  // step 예산 policy 도 있어서 헤더를 Epoch 으로 고정하지 않는다. 단위는 셀이 값 앞에 표시한다.
  { key: "epoch", header: "#", width: "110px", render: () => null },
  { key: "createdAtMs", header: "등록일", width: "120px", render: () => null },
];

// 이 표는 파라미터 폼과 같은 모달에 있어서 폼 입력마다 부모가 다시 렌더링된다. 두 prop 은 창이
// 열린 동안 바뀌지 않는다. 그래서 memo 로 표 재조정을 막는다.
export const EvalPolicyTable = memo(function EvalPolicyTable({
  policies,
  nowMs,
}: {
  policies: readonly EvaluationTargetPolicy[];
  nowMs: number;
}): ReactNode {
  const groupCount = groupPoliciesByDataset(policies).length;
  const source = useMemo(() => new EvalPolicyTableSource(policies), [policies]);

  useEffect(() => {
    void source.initialize();
    return () => {
      source.dispose();
    };
  }, [source]);

  return (
    <div className={styles.root}>
      {/* dataset 이 섞이면 TASK 하나가 모든 policy 에 그대로 적용된다. 그래서 먼저 경고한다. */}
      {groupCount > 1 ? (
        <p className={styles.warning} role="alert">
          {`선택한 Policy 가 서로 다른 Dataset ${groupCount}개로 학습되었습니다. 모든 Policy 에 같은 TASK 하나가 적용됩니다.`}
        </p>
      ) : null}
      <HatDataTable
        source={source}
        columns={EVAL_POLICY_COLUMNS}
        ariaLabel="Evaluation 대상 Policy"
        emptyMessage="선택한 Policy 가 없습니다."
        // 행 번호는 그룹을 세므로 뜻이 없다.
        showIndex={false}
        // 확인 다이얼로그의 라벨 열 옆 카드 폭에서는 c6 기본 최소 폭(760px)이 넘친다.
        minWidth="0px"
        // 그룹 행은 펼침 값을 정하지 않고 접기 UI 도 없다. 그래서 이 기본값으로 항상 펼친다.
        defaultRowChildrenExpanded
        renderRowChild={(child) =>
          child instanceof EvalPolicyRow ? <EvalPolicyLine policy={child} nowMs={nowMs} /> : null
        }
      />
    </div>
  );
});

// 그룹 행 아래 policy 한 건. c6 rowChildCell(표 컬럼 트랙의 subgrid) 안에서 트랙을 이어받는다.
// 그래서 각 값이 헤더 컬럼 아래에 놓인다.
function EvalPolicyLine({ policy, nowMs }: { policy: EvalPolicyRow; nowMs: number }): ReactNode {
  return (
    <span className={styles.policyLine}>
      <span className={styles.policyName}>{policy.name}</span>
      <span className={styles.policyId} title={policy.id}>
        {policy.id.slice(0, 8)}
      </span>
      <span className={styles.policyEpoch}>
        {policy.epoch !== null
          ? `${budgetUnitCellLabel(policy.budgetUnit)} ${policy.epoch}`
          : EMPTY_CELL}
      </span>
      <span className={styles.policyCreated}>{formatRelativeTime(policy.createdAtMs, nowMs)}</span>
    </span>
  );
}
